import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery, useQueryClient } from 'react-query'
import { hideModal } from '../../../../actions/_modal'
import colors from '../../../../configuration/colors'
import withApiData from '../../../HOC/withApiData'
import TransparentButton from '../../../styled/buttons/TransparentButton'
import { fetchMentorGuidance } from './API'
import { Container } from './styles'
import Edit from './Edit'
import View from './View'
import useLoomData from '../../../../hooks/useLoomData'
import useLexicon from '../../../../hooks/useLexicon'
import { scrollElementIntoView } from '../../../Student/UserCompiledQuest/util'

export const MentorGuidanceContext = createContext({
  courseSectionId: null,
  levelId: null,
  questId: null,
  activityId: null,
  isUserCompiledQuest: false,
  isEnrolled: false,
  background: colors.smoke,
  paddingLR: 0,
  marginBottom: 0,
  rightOffsetDeleteButton: -75,
  leftOffsetDeleteButton: 0,
  recoveryPath: null,
  sessionId: null,
  isInModal: false,
  startInEditMode: false,
  onNavigationComplete: () => { }
})

export const MENTOR_GUIDANCE_MODE = {
  DEFAULT: 0,
  EDIT: 1,
  VIEW: 2
}

export const MENTOR_GUIDANCE_TYPE = {
  LEVEL: 0,
  QUEST: 1,
  ACTIVITY: 2,
  JOURNEY: 3
}
const Index = ({ apiData, type, deleteConfirmationFollowupAction, isJourney, isEditingJourneyGuidance }) => {
  const dispatch = useDispatch()
  const lexicon = useLexicon()
  const mentorGuidanceContext = useContext(MentorGuidanceContext)
  const [currentMode, setCurrentModeForce] = useState(
    mentorGuidanceContext.startInEditMode
      ? MENTOR_GUIDANCE_MODE.EDIT
      : MENTOR_GUIDANCE_MODE.DEFAULT
  )
  const guidanceRef = useRef()
  const isMentor = useSelector(state => state.auth.isMentor)
  const isMentorView = isMentor && !mentorGuidanceContext.isUserCompiledQuest
    && mentorGuidanceContext.isEnrolled
  const loomRecoveryData = useLoomData(mentorGuidanceContext.sessionId)
  const [shouldScroll, setShouldScroll] = useState(
    mentorGuidanceContext.startInEditMode
  )

  const setCurrentMode = mode => {
    if (!loomRecoveryData || mode === MENTOR_GUIDANCE_MODE.EDIT) {
      setCurrentModeForce(mode)
    }
  }

  const queryClient = useQueryClient()
  const queryKey = ['mentor-guidance', {
    courseSectionId: mentorGuidanceContext.courseSectionId,
    levelId: mentorGuidanceContext.levelId,
    questId: mentorGuidanceContext.questId,
    activityId: mentorGuidanceContext.activityId
  }]
  const mentorGuidanceQuery = useQuery(
    queryKey,
    () => fetchMentorGuidance(apiData, mentorGuidanceContext, type),
    {
      refetchOnWindowFocus: false
    }
  )

  useEffect(() => {
    if (currentMode !== MENTOR_GUIDANCE_MODE.EDIT) {
      if (mentorGuidanceQuery.data && mentorGuidanceQuery.data.length > 0) {
        setCurrentMode(MENTOR_GUIDANCE_MODE.VIEW)
      } else {
        setCurrentMode(MENTOR_GUIDANCE_MODE.DEFAULT)
      }
    }
  }, [mentorGuidanceQuery.data])

  useEffect(() => {
    if (loomRecoveryData) {
      setCurrentMode(MENTOR_GUIDANCE_MODE.EDIT)
    }
  }, [loomRecoveryData])

  useLayoutEffect(() => {
    if (shouldScroll && guidanceRef.current) {
      setShouldScroll(false)
      scrollElementIntoView(guidanceRef.current)
      mentorGuidanceContext.onNavigationComplete()
    }
  })

  return mentorGuidanceQuery.isLoading ? null : (
    <Container
      ref={guidanceRef}
      background={mentorGuidanceContext.background}
      paddingLR={mentorGuidanceContext.paddingLR}
      journey={type === 3}
      isEditing={currentMode === MENTOR_GUIDANCE_MODE.EDIT}
      marginBottom={mentorGuidanceContext.marginBottom}
      onClick={() => {
        if (currentMode === MENTOR_GUIDANCE_MODE.VIEW && isMentorView) {
          setCurrentMode(MENTOR_GUIDANCE_MODE.EDIT)
        }
      }}
      style={{
        cursor:
          currentMode === MENTOR_GUIDANCE_MODE.VIEW && isMentorView
            ? 'pointer'
            : 'default',
        display:
          !isMentorView && currentMode === MENTOR_GUIDANCE_MODE.DEFAULT
            ? 'none'
            : 'block'
      }}
    >
      {currentMode === MENTOR_GUIDANCE_MODE.DEFAULT && isMentorView && (
        <TransparentButton
          onClick={() => {
            setCurrentMode(MENTOR_GUIDANCE_MODE.EDIT)
          }}
        >
          {type === 3 ? '+ Add Guidance' : `+ Add ${lexicon.mentor_guidance}`}
        </TransparentButton>
      )}

      {currentMode === MENTOR_GUIDANCE_MODE.EDIT && isMentorView && (
        <Edit
          loomData={loomRecoveryData}
          guidance={mentorGuidanceQuery.data[0]}
          recoveryAction={deleteConfirmationFollowupAction}
          type={type}
          isJourney={isJourney}
          isEditingJourneyGuidance={isEditingJourneyGuidance}
          onCancel={() => {
            setCurrentMode(
              mentorGuidanceQuery.data.length
                ? MENTOR_GUIDANCE_MODE.VIEW
                : MENTOR_GUIDANCE_MODE.DEFAULT
            )
          }}
          onSave={() => {
            queryClient.invalidateQueries(queryKey)
            setCurrentMode(MENTOR_GUIDANCE_MODE.VIEW)
          }}
        />
      )}

      {currentMode === MENTOR_GUIDANCE_MODE.VIEW && (
        <View
          isJourney={isJourney}
          isMentor={isMentorView}
          guidance={mentorGuidanceQuery.data[0]}
          type={type}
          onEditClick={() => {
            setCurrentMode(MENTOR_GUIDANCE_MODE.EDIT)
          }}
          onDelete={() => {
            queryClient.invalidateQueries(queryKey)
            setCurrentMode(MENTOR_GUIDANCE_MODE.DEFAULT)
          }}
          closeConfirmationModalAction={() => {
            dispatch(hideModal())

            if (deleteConfirmationFollowupAction) {
              deleteConfirmationFollowupAction()
            }
          }}
        />
      )}
    </Container>
  )
}

Index.propTypes = {
  apiData: PropTypes.object.isRequired,
  type: PropTypes.number.isRequired,
  deleteConfirmationFollowupAction: PropTypes.func
}

export default withApiData(Index)
